import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CheckCircle2, ArrowUpRight, Clock, FileText, Star,
  MessageSquare, TrendingUp, Users
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = "text-[#1d366b] bg-blue-50", testid, highlight = false }) => (
  <Card className={`p-5 ${highlight ? "border-emerald-300 bg-emerald-50/40" : ""}`}>
    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${color} mb-3`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-2xl font-semibold text-slate-900" data-testid={testid}>{value ?? "—"}</p>
    <p className="text-sm text-slate-500">{label}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </Card>
);

const StarsAvg = ({ value }) => (
  <span className="inline-flex items-center gap-1">
    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
    <span className="font-semibold text-slate-800">{value != null ? value : "—"}</span>
    <span className="text-xs text-slate-400">/5</span>
  </span>
);

/**
 * Admin panel tab: pilot KPIs.
 * Volume/week per doctor, avg response time, escalation vs async resolution rate,
 * and patient survey aggregates (satisfaction, NPS, comments).
 * Data source: GET /api/admin/pilot-metrics?days=N
 */
const PilotMetricsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("0");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/admin/pilot-metrics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("No pudimos cargar las métricas del piloto");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <p className="text-sm text-slate-500">Cargando métricas del piloto…</p>;
  }
  if (!data) return null;

  const s = data.survey || {};

  return (
    <div className="space-y-6" data-testid="pilot-metrics-panel">
      {/* Period filter */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Métricas del Piloto</h2>
          <p className="text-sm text-slate-500">Consultas clínicas (recetas excluidas)</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-44 bg-white" data-testid="pilot-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
            <SelectItem value="0">Todo el período</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Core KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={CheckCircle2}
          label="Resolución asincrónica"
          value={data.async_resolution_rate != null ? `${data.async_resolution_rate}%` : "—"}
          sub={`${data.totals.answered} resueltas sin presencial`}
          color="text-emerald-600 bg-emerald-100"
          testid="pilot-async-rate"
          highlight
        />
        <StatCard
          icon={ArrowUpRight}
          label="Tasa de escalamiento"
          value={data.escalation_rate != null ? `${data.escalation_rate}%` : "—"}
          sub={`${data.totals.escalated} derivadas a presencial`}
          color="text-purple-600 bg-purple-50"
          testid="pilot-escalation-rate"
        />
        <StatCard
          icon={Clock}
          label="Tiempo de respuesta prom."
          value={data.avg_response_time_hours != null ? `${data.avg_response_time_hours} h` : "—"}
          color="text-blue-600 bg-blue-50"
          testid="pilot-avg-response-time"
        />
        <StatCard
          icon={FileText}
          label="Consultas clínicas"
          value={data.totals.clinical_consults}
          sub={`${data.totals.pending} pendientes de respuesta`}
          color="text-slate-600 bg-slate-100"
          testid="pilot-total-consults"
        />
      </div>

      {/* Survey KPIs */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
          Satisfacción del paciente
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={TrendingUp}
            label="NPS"
            value={s.nps_score != null ? s.nps_score : "—"}
            sub={s.nps_responses ? `${s.nps_promoters} promotores · ${s.nps_detractors} detractores (${s.nps_responses} resp.)` : "Sin respuestas NPS aún"}
            color="text-indigo-600 bg-indigo-50"
            testid="pilot-nps"
          />
          <StatCard
            icon={MessageSquare}
            label="Tasa de respuesta encuesta"
            value={s.response_rate != null ? `${s.response_rate}%` : "—"}
            sub={`${s.count} encuestas / ${s.eligible} consultas elegibles`}
            color="text-cyan-600 bg-cyan-50"
            testid="pilot-survey-rate"
          />
          <Card className="p-5 sm:col-span-2">
            <p className="text-sm text-slate-500 mb-3">Promedio por dimensión (1-5 estrellas)</p>
            <div className="grid grid-cols-3 gap-3" data-testid="pilot-survey-dimensions">
              <div>
                <p className="text-xs text-slate-400 mb-1">Respuesta</p>
                <StarsAvg value={s.avg_rating_response} />
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Tiempo</p>
                <StarsAvg value={s.avg_rating_time} />
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Facilidad de uso</p>
                <StarsAvg value={s.avg_rating_ease} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Volume per doctor */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Volumen por médico
          </h3>
        </div>
        {data.volume_per_doctor.length === 0 ? (
          <p className="text-sm text-slate-400">Sin consultas en el período seleccionado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pilot-volume-table">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                  <th className="py-2 pr-4">Médico</th>
                  <th className="py-2 pr-4 text-right">Consultas</th>
                  <th className="py-2 text-right">Promedio / semana</th>
                </tr>
              </thead>
              <tbody>
                {data.volume_per_doctor.map((d) => (
                  <tr key={d.doctor_id} className="border-b border-slate-50 last:border-b-0">
                    <td className="py-2.5 pr-4 text-slate-800">{d.doctor_name}</td>
                    <td className="py-2.5 pr-4 text-right font-semibold text-slate-900">{d.total}</td>
                    <td className="py-2.5 text-right text-slate-600">{d.per_week}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Comments */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
          Comentarios recientes de pacientes
        </h3>
        {(!s.comments || s.comments.length === 0) ? (
          <p className="text-sm text-slate-400">Todavía no hay comentarios.</p>
        ) : (
          <div className="space-y-3" data-testid="pilot-comments-list">
            {s.comments.map((c, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-sm text-slate-700">"{c.comment}"</p>
                <p className="text-xs text-slate-400 mt-1.5">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString("es-AR") : ""}
                  {c.category ? ` · ${c.category}` : ""}
                  {c.rating_response ? ` · ${c.rating_response}★ respuesta` : ""}
                  {c.consult_status === "ESCALATED" ? " · derivada" : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PilotMetricsPanel;
